<template>
    <div :class="['chat-page', { 'chat-page--embedded': isEmbedded }]">
		<!-- Page Header (full mode only) -->
		<div v-if="!isEmbedded" class="chat-page-header">
			<div class="header-icon-wrapper">
				<i class="fa fa-comments"></i>
			</div>
			<div class="header-titles">
				<h1>Team Chat</h1>
				<p>Internal messaging for your team members</p>
			</div>
			<button class="new-chat-btn" @click="addRoom">
				<i class="fa fa-plus"></i>
				<span>New Conversation</span>
			</button>
			<!-- ⋮ menu: Settings (all users) + admin export. Folds the old standalone gear in here so the header has one "more" control instead of two adjacent icons. -->
			<b-dropdown
				right
				no-caret
				variant="link"
				class="chat-admin-menu"
				toggle-class="chat-admin-menu-toggle"
				data-test="chat-header-menu"
			>
				<template #button-content>
					<i class="fa fa-ellipsis-v"></i>
					<span class="sr-only">Chat menu</span>
				</template>
				<b-dropdown-item
					@click="openChatSettings"
					data-test="chat-settings-menu-item"
				>
					<i class="fa fa-cog mr-2"></i>
					Settings
				</b-dropdown-item>
				<b-dropdown-item
					v-if="$can('export', 'chatroom') && !isDemoSession"
					@click="openBulkExport"
					data-test="chat-export-all-btn"
				>
					<i class="fa fa-download mr-2"></i>
					Export All Team Chats
				</b-dropdown-item>
			</b-dropdown>
		</div>
		<!-- Status pill: shown while a recently-fired export job is still pending -->
		<div
			v-if="!isEmbedded && exportPending"
			class="chat-export-status-pill"
			data-test="export-status-pill"
		>
			<i class="fa fa-cog fa-spin mr-2"></i>
			Export running… we'll email you when it's done
			<button
				class="ml-2 close-pill-btn"
				@click="dismissExportPill"
				title="Dismiss"
				data-test="export-status-pill-dismiss"
			>×</button>
		</div>
		<!--
			Mount point for vue2-slideout-panel (ChatSettings renders here) — FULL mode only.

			vue2-slideout-panel routes $showPanel through ONE module-level shared event bus
			(node_modules/vue2-slideout-panel/dist/vue-slideout-panel.js:624 `new Vue()`;
			showPanel emits 'showSlideOutPanel' on it at :14349) and EVERY mounted
			<slideout-panel> outlet subscribes to that bus in created() (:1379) and pushes the
			panel into its own local `panels` array. Two mounted outlets therefore render TWO
			stacked copies of the same panel — clicks land on the wrong copy and multiselect
			option lists render underneath the twin.

			That is exactly what Practice Pulse hit: PracticePulse.vue mounts its own outlet
			(:469) AND embeds this component (<chat-container mode="embedded"> at :400), so
			gear → Configure Settings opened the panel twice and its dropdowns stopped working.
			An EMBEDDED chat must not add a second live outlet to its host page.

			Nothing is lost by gating this: because the bus is shared and untargeted, a
			$showPanel raised from embedded chat still renders through the HOST page's outlet.
			Same plugin behaviour documented at
			OverdueRecall/components/RecallReminderReportDrawer.vue:210-215.
		-->
		<slideout-panel v-if="!isEmbedded"></slideout-panel>
		<ErrorDisplay/>
		
		<ChatSkeleton v-if="busy && !isEmbedded" />
		
		<div class="chat-forms" v-if="addNewRoom || inviteRoomId || (removeRoomId && !deleteRoomInProgess)">
			<form @submit.prevent="createRoom" v-if="addNewRoom">
				<h3>Create a New Room</h3>
				<div class="form-row">
					<multiselect
						v-if="employeesToChatWith"
						v-model="selectedUsers"
						:options="employeesToChatWith"
						:multiple="true"
						:taggable="false"
						:show-labels="false"
						label="Name"
						track-by="EmployeeID"
						placeholder="Select team members"
					/>
					<input
						type="text"
						v-model="newRoomName"
						placeholder="Room name (optional)"
						class="chat-new-room-name"
					/>
					<button type="submit" :disabled="disableForm">
						Create Room
					</button>
					<button type="button" class="button-cancel" @click="addNewRoom = false; resetError(); selectedUsers=[]; newRoomName='';">
						Cancel
					</button>
				</div>
			</form>

			<form @submit.prevent="addRoomUser" v-if="inviteRoomId">
				<h3>Add Users to {{ getRoomName(this.selectedRoom) }}</h3>
				<div class="form-row">
					<multiselect
						v-if="employeesToChatWith"
						v-model="selectedUsers"
						:options="employeesToChatWith"
						:multiple="true"
						:taggable="false"
						:show-labels="false"
						label="Name"
						track-by="EmployeeID"
						placeholder="Select one or more Users"
					/>
					<button type="submit" :disabled="disableForm">
						Add User
					</button>
					<button type="button" class="button-cancel" @click="inviteRoomId = null">
						Cancel
					</button>
				</div>
			</form>

			<form @submit.prevent="deleteRoomUser" v-if="removeRoomId">
				<h3>Remove Users from {{ getRoomName(this.selectedRoom) }}</h3>
				<div class="form-row">
					<multiselect
						v-if="getRoomUsers(removeRoomId).length > 0"
						v-model="selectedUsers"
						:options="getRoomUsers(removeRoomId)"
						:multiple="true"
						:taggable="false"
						:show-labels="false"
						label="username"
						track-by="employeeID"
						placeholder="Select one or more Users"
					/>
					<button type="submit" :disabled="disableForm || selectedUsers.length === 0">
						Remove User
					</button>
					<button type="button" class="button-cancel" @click="removeRoomId = null; selectedUsers = [];">
						Cancel
					</button>
				</div>
			</form>
		</div>
		
        <chat-window v-show="!busy"
			:styles="styles"
			:show-files="true"
			:show-audio="false"
			:accepted-files="acceptedFileTypes"
			:load-first-room="false"
			:text-messages="textMessages"
            :current-user-id="effectiveCurrentUserId"
			:rooms="visibleRooms"
			:loading-rooms="loadingRooms"
			:rooms-loaded="roomsLoaded"
			:messages="windowedMessages"
			:messages-loaded="messagesLoaded"
			:menu-actions="menuActions"
			@add-room="addRoom"
			@fetch-messages="fetchMessages"
			@send-message="sendMessage"
			@edit-message="editMessage"
			@delete-message="deleteMessage"
			@send-message-reaction="sendMessageReaction"
			@menu-action-handler="menuActionHandler"
			@open-file="openFile"
		>
			<!-- Filter chips above search (All / Unread) -->
			<template v-slot:rooms-header>
				<UnreadFilterChips
					v-model="activeFilter"
					:unread-count="unreadRoomCount"
				/>
			</template>
			<!-- "All caught up!" empty state when filter is on but zero unread -->
			<template v-slot:rooms-empty>
				<AllCaughtUpEmptyState
					v-if="activeFilter === 'unread' && rooms && rooms.length > 0"
					@show-all="activeFilter = 'all'"
				/>
			</template>
		</chat-window>

		<ChatImageLightbox
			:visible="lightboxVisible"
			:images="lightboxImages"
			:startIndex="lightboxStartIndex"
			@close="lightboxVisible = false"
		/>

		<b-modal ref="sure-modal-remove-room" size="sm" body-class="bg-white" title="Are you sure?" @ok="deleteRoom" @cancel="deleteRoomInProgess = false; removeRoomId=null; hideModal('sure-modal-remove-room')">
			Are you sure you want to delete this chat room? You will NOT be able to restore this once it's deleted.
		</b-modal>

		<b-modal v-if="showRenameModal" :visible="showRenameModal" @hide="showRenameModal = false" title="Rename Room">
			<div>
				<label for="rename-room-input">New Room Name</label>
				<input id="rename-room-input" v-model="newCustomNameForEdit" class="form-control mb-2" placeholder="Enter new room name" />
			</div>
			<template #modal-footer>
				<button class="btn btn-primary mr-2" @click="submitRenameRoom">Save</button>
				<button class="btn btn-secondary" @click="showRenameModal = false">Cancel</button>
			</template>
		</b-modal>

		<ExportChatModal
			v-if="exportModalVisible"
			:visible="exportModalVisible"
			:scope="exportScope"
			:room-id="exportRoomId"
			:room-name="exportRoomName"
			:site-id="userSiteId"
			:site-name="userSiteName"
			:timezone="userTimezone"
			:requester-email="userEmail"
			:requester-name="userName"
			@close="closeExportModal"
		/>

		<div v-if="!isEmbedded" class="chat-retention-notice">
			<i class="fa fa-info-circle"></i>
			Messages are retained for 1 year. File attachments (max 10MB images, 25MB docs, 50MB video/audio) are retained for 90 days.
		</div>
		<div v-if="!isEmbedded" class="chat-page-copyright">&copy; Teamio.app</div>
	</div>
</template>

<script>
/* eslint-disable */

import Vue from "vue";
import Widget from "@/components/Widget/Widget";
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import { mapState, mapActions, mapMutations } from 'vuex';
import ChatWindow from 'vue-advanced-chat';
import 'vue-advanced-chat/dist/vue-advanced-chat.css';
import { parseTimestamp, isSameDay } from './util/dates';
import Multiselect from "vue-multiselect";
import CommonOverlay from '@/components/CommonOverlay.vue';
import ChatImageLightbox from './ChatImageLightbox.vue';
import ChatSkeleton from './ChatSkeleton.vue';
import ChatSettings from './components/ChatSettings.vue';
import ExportChatModal from './components/ExportChatModal.vue';
import UnreadFilterChips from '@/components/UnreadFilterChips.vue';
import AllCaughtUpEmptyState from '@/components/AllCaughtUpEmptyState.vue';
import mixin from '../../mixin.js';
import { findRoomWithSameParticipants } from '@/store/chat-utils';
import { validateFile, uploadToS3, deleteFromS3, getFileCategory, getAcceptedFileTypes, getAcceptedFileTypesDescription } from '@/utils/fileUpload';

// Chat message retention: 1 year (365 days in ms)
const CHAT_MESSAGE_RETENTION_MS = 365 * 24 * 60 * 60 * 1000;

function getChatExpireAt() {
	return new Date(Date.now() + CHAT_MESSAGE_RETENTION_MS);
}
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'ChatContainer',
  components: { Widget, ErrorDisplay, Multiselect, ChatWindow, CommonOverlay, ChatImageLightbox, ChatSkeleton, UnreadFilterChips, AllCaughtUpEmptyState, ExportChatModal },
  props: {
    currentUserId: { type: String, default: null },
    theme: { type: String, default: null },
    mode: { type: String, default: 'full', validator: v => ['full', 'embedded'].includes(v) }
  },
  mixins: [mixin],

	created() {
		this.init();
	},

  	mounted() {
		// Force a DOM repaint
		this.$nextTick(() => {
			this.loadRooms();
		});

		// Team Chat Export — restore "Export running…" pill if a job was
		// fired in a recent session and hasn't timed out (15 min).
		this.refreshExportPendingState();
		this.exportPendingTimer = setInterval(this.refreshExportPendingState, 60 * 1000);
		// Clipboard paste handler for images
		this._pasteHandler = (e) => {
			const items = e.clipboardData && e.clipboardData.items;
			if (!items) return;
			for (let i = 0; i < items.length; i++) {
				if (items[i].type.startsWith('image/')) {
					e.preventDefault();
					const blob = items[i].getAsFile();
					if (!blob) return;
					const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
					// Trigger send with this file — the chat-window library accepts files via sendMessage
					if (this.selectedRoom) {
						this.sendMessage({
							content: '',
							roomId: this.selectedRoom,
							files: [{
								name: file.name,
								size: file.size,
								type: file.type,
								blob: file,
								localUrl: URL.createObjectURL(file)
							}]
						});
					}
					break;
				}
			}
		};
		document.addEventListener('paste', this._pasteHandler);

		// Add tooltips to library icons after DOM renders
		this.$nextTick(() => {
			setTimeout(() => {
				const addRoomIcon = document.querySelector('.vac-svg-button.vac-add-icon');
				if (addRoomIcon) addRoomIcon.setAttribute('title', 'Create New Room');
			}, 500);
		});
	},

	destroyed() {
		this.resetRooms();
		if (this._pasteHandler) {
			document.removeEventListener('paste', this._pasteHandler);
		}
		if (this.recentEmojiEl) {
			this.recentEmojiEl.remove();
		}
		if (this.exportPendingTimer) {
			clearInterval(this.exportPendingTimer);
			this.exportPendingTimer = null;
		}
	},

  data() {
    return {
		roomsPerPage: 15,
		loadingLastMessageByRoom: 0,
		selectedRoom: null,
		messagesPerPage: 20,
		// Team Chat Export state
		exportModalVisible: false,
		exportScope: 'room',        // 'room' | 'allRooms'
		exportRoomId: null,
		exportRoomName: null,
		exportPending: false,
		exportPendingTimer: null,
		roomMessage: '',
		typingMessageCache: '',
		disableForm: false,
		addNewRoom: null,
		newRoomName: '',
		selectedUsers: [],
		inviteRoomId: null,
		invitedUsername: '',
		removeRoomId: null,
		removeUserId: '',
		removeUsers: [],		
        styles: {
            container: {
                marginTop: '10px',
                borderRadius: '4px',
            },
			room: {
				colorUsername: '#0a0a0a',
				colorMessage: '#67717a',
				colorTimestamp: '#a2aeb8',
				colorStateOnline: '#4caf50',
				colorStateOffline: '#ccc',
				backgroundCounterBadge: '#3cc095',
				colorCounterBadge: '#fff'
			},
		},
		textMessages: {
			ROOMS_EMPTY: `You don't have any chat rooms. Please click on the + icon above to create a new room first`,
			ROOM_EMPTY: 'Please select a chat room on the left to start messaging',
			MESSAGES_EMPTY: 'No messages yet. Start typing your message below',
			SEARCH: 'Search by room name...',
		},
		deleteRoomInProgess: false,
		busy: true, // Start with busy set to true
		// Add data properties for renaming room
		roomToRenameId: null,
		roomToRenameCurrentName: '',
		newCustomNameForEdit: '',
		showRenameModal: false,
		// Lightbox state
		lightboxVisible: false,
		lightboxImages: [],
		lightboxStartIndex: 0,
		// Recent emojis
		recentEmojis: JSON.parse(localStorage.getItem('teamio-recent-emojis') || 'null') || ['👍', '👀', '😂', '❤️', '🔥', '✅', '🎉', '😊', '🙏', '💪'],
		recentEmojiEl: null,
		activeFilter: 'all',
		// Cap on messages rendered into vue-advanced-chat. The library re-renders
		// every Message child on every keystroke (scoped-slot fan-out defeats
		// child render skip), so per-keystroke cost scales linearly with rendered
		// count (~0.2ms / rendered msg, profiled on prod). Cap = 25 keeps the
		// per-keystroke render cost within ~5ms of the empty-room baseline so
		// busy rooms feel as responsive as 1:1s. Window still grows on scroll-up
		// so users can walk back through history.
		renderedMessageSize: 25,
    }
  },

  methods: {
	
	...mapActions('chat', ['_fetchMoreRooms', '_updateUserOnlineStatus', '_listenLastMessage', '_fetchMessages',
							'_sendMessage', '_createRoom','_addRoomUser', '_editMessage', '_deleteMessage',
						'_deleteRoomUser', '_deleteRoom', '_updateRoomName', '_startChatExport']),

	...mapMutations('chat', ['setLoadingRooms', 'setRoomsLoaded', 'setRooms', 'setRoomsLoadedCount', 'setStartRooms', 
					'setEndRooms', 'setListeners', 'setRoomsListener', 'setRoomsLoaded', 'setMessagesLoaded', 'setMessages', 
					'setStartMessages', 'setEndMessages', 'setLoadingLastMessageByRoom']),

	init() {
		this.resetError();
	},

    resetRooms() {
		this.setLoadingRooms(true);
		this.setLoadingLastMessageByRoom(0);
		this.setRoomsLoadedCount(0);
		this.setRooms([]);
		this.setRoomsLoaded(false);
		this.setStartRooms(null);
		this.setEndRooms(null);
		this.roomsListener.forEach(listener => listener());
		this.setRoomsListener([]);
		this.resetMessages();
	},

	resetMessages() {
		this.setMessages([]);
		this.setMessagesLoaded(false);
		this.setStartMessages(null);
		this.setEndMessages(null);
		if(this.listeners)
			this.listeners.forEach(listener => listener());
		this.setListeners([]);
	},

	resetForms() {
		this.disableForm = false;
		this.addNewRoom = null;
		this.newRoomName = '';
		this.inviteRoomId = null;
		this.invitedUsername = '';
		this.removeRoomId = null;
		this.removeUserId = '';
	},
	
	addRoom() {
		this.resetForms();
		this.addNewRoom = true;
    },

	openChatSettings() {
		const handle = this.$showPanel({
			component: ChatSettings,
			openOn: 'right',
			width: 720,
			disableBgClick: true,
			disableEscClick: true,
		});
		handle.promise.then(() => { /* panel closed */ });
	},

	// ─── Team Chat Export ──────────────────────────────────────────────
	openPerRoomExport(roomId) {
		const room = this.rooms.find(r => r.roomId === roomId);
		this.exportScope = 'room';
		this.exportRoomId = roomId;
		this.exportRoomName = (room && room.roomName) || null;
		this.exportModalVisible = true;
	},

	openBulkExport() {
		this.exportScope = 'allRooms';
		this.exportRoomId = null;
		this.exportRoomName = null;
		this.exportModalVisible = true;
	},

	closeExportModal() {
		this.exportModalVisible = false;
		// If a job was just started, the modal called localStorage. Refresh
		// the pill state so it appears immediately without waiting for the
		// next route visit / page mount.
		this.refreshExportPendingState();
	},

	refreshExportPendingState() {
		let raw;
		try { raw = localStorage.getItem('teamio.chatExport.pending'); }
		catch (e) { this.exportPending = false; return; }
		if (!raw) { this.exportPending = false; return; }
		try {
			const parsed = JSON.parse(raw);
			const FIFTEEN_MIN = 15 * 60 * 1000;
			if (Date.now() - (parsed.requestedAt || 0) > FIFTEEN_MIN) {
				localStorage.removeItem('teamio.chatExport.pending');
				this.exportPending = false;
			} else {
				this.exportPending = true;
			}
		} catch (e) {
			localStorage.removeItem('teamio.chatExport.pending');
			this.exportPending = false;
		}
	},

	dismissExportPill() {
		try { localStorage.removeItem('teamio.chatExport.pending'); }
		catch (e) { /* noop */ }
		this.exportPending = false;
	},

	async createRoom() {
		console.log('[ChatContainer] createRoom called. Selected users:', JSON.parse(JSON.stringify(this.selectedUsers)), 'Custom name input:', this.newRoomName);

		if(this.selectedUsers.length === 0) {				
			this.$toast.error('Please select a person to chat with first');
			return false;
		} else {
			this.resetError();
		}

		// Only store a room name if the user explicitly typed one.
		// Auto-generated names are computed at display time per-user by generateRoomDisplayName.
		let intendedRoomName = '';
		if (this.newRoomName && this.newRoomName.trim() !== '') {
			intendedRoomName = this.newRoomName.trim();
			console.log('[ChatContainer] Using custom room name:', intendedRoomName);
		} else {
			console.log('[ChatContainer] No custom name — will be auto-generated at display time');
		}

		// Duplicate participant check — prevent creating a second room with exact same people
		const currentUserId = this.currentUserId || this.userID;
		const allNewUserIds = [..._.map(this.selectedUsers, 'EmployeeID'), currentUserId];
		const existingRoom = findRoomWithSameParticipants(this.rooms, allNewUserIds);
		if (existingRoom) {
			console.warn('[ChatContainer] Room already exists with same participants:', existingRoom.roomId);
			this.$toast.error('A conversation with the same participants already exists.');
			return;
		}

		// Custom name uniqueness check (only if user typed a name)
		if (intendedRoomName) {
			const foundRoomByName = this.rooms.find(r => r.roomName === intendedRoomName);
			if (foundRoomByName) {
				this.$toast.error('A chat room with this name already exists. Please choose a different name.');
				return;
			}
		}

		this.disableForm = true;
		this.busy = true;
		try {
			const payload = {
				siteID: this.siteID,
				users: [...this.selectedUsers, this.user],
				userIds: _.map(this.selectedUsers, 'EmployeeID'),
				currentUserId: currentUserId,
				roomName: intendedRoomName
			};
			console.log('[ChatContainer] Payload for _createRoom:', JSON.parse(JSON.stringify(payload)));
			await this._createRoom(payload);
			this.addNewRoom = false;
			this.newRoomName = ''; // Clear the input
			// this.fetchRooms(); // fetchRooms is likely called by Vuex action or a watcher after room creation updates state
		} catch (error) {
			console.error('[ChatContainer] Error creating room:', error);
			this.$toast.error('Failed to create new chat room. Please try again.');
		} finally {
			this.busy = false;
			this.disableForm = false; // Re-enable form
		}
	},
    
	inviteUser(roomId) {
		this.resetForms();
		this.inviteRoomId = roomId;
    },

	getRoomName(roomId) {
		if(this.rooms) {
			let r =  this.rooms.find(r => r.roomId === roomId);
			if(r)
				return r.roomName;
		}
	},
    
	async addRoomUser() {
		this.disableForm = true;
		this.busy = true;
		try {
			let payload = {
				users: this.selectedUsers,
				userIds: _.map(this.selectedUsers, 'EmployeeID'),			
				inviteRoomId: this.inviteRoomId
			}
			//console.log('payload', payload);
			await this._addRoomUser(payload);
			this.disableForm = false;
			this.inviteRoomId = null;
			this.selectedUsers = [];
		} catch (error) {
			console.error('Error adding room user:', error);
			this.$toast.error('Failed to add user to chat room. Please try again.');
		} finally {
			this.busy = false;
		}
    },

	getRoomUsers(roomId) {		
		if(this.rooms) {
			let r = this.rooms.find(r => r.roomId === roomId);
			if(r) {				
				return r.users;
			} else
				return [];
		}
	},

	removeUser(roomId) {
			this.resetForms()
			this.removeRoomId = roomId
			//this.removeUsers = this.rooms.find(room => room.roomId === roomId).users
    },

	async deleteRoomUser() {
			this.disableForm = true
			this.busy = true
			try {
				let payload = {
					removeRoomId: this.removeRoomId,
					users: this.selectedUsers
				}

				await this._deleteRoomUser(payload);		
				this.disableForm = false;	
				this.removeRoomId = null;
				this.selectedUsers = [];			
			} catch (error) {
				console.error('Error removing room user:', error);
				this.$toast.error('Failed to remove user from chat room. Please try again.');
			} finally {
				this.busy = false;
			}
    }, 

	async deleteRoom() {
		this.disableForm = true
		this.busy = true
		try {
			let payload = {
				removeRoomId: this.removeRoomId,			
			}
			await this._deleteRoom(payload);
			this.disableForm = false;
			this.deleteRoomInProgess = false;
			this.removeRoomId = null
			this.removeUserId = ''
		} catch (error) {
			console.error('Error deleting room:', error);
			this.$toast.error('Failed to delete chat room. Please try again.');
		} finally {
			this.busy = false;
		}
    }, 
	
	addRoom() {
		this.resetForms();
		this.addNewRoom = true;
	},
    
	async fetchRooms() {
		this.resetRooms();
		this.busy = true;
		try {
			const result = await this.fetchMoreRooms();
			return result;
		} catch (error) {
			console.error('Error fetching rooms:', error);
			this.$toast.error('Failed to load chat rooms. Please try again.');
			throw error;
		} finally {
			this.busy = false;
		}
	},
	
	async fetchMoreRooms() {
		let payload = {
			dateFormatStr: this.dateFormatStr,
          currentUserId: this.effectiveCurrentUserId,
			roomsPerPage : this.roomsPerPage,
			startRooms: this.startRooms,
			endRooms: this.endRooms
		}
		await this._fetchMoreRooms(payload);
	},
    
	listenLastMessage(room) {
		this._listenLastMessage({room});
	},
    
	async fetchMessages({ room, options = {} }) {
		//console.log(`this is the parent fetchMessages`);
		if (options.reset)
			this.resetMessages();
		if (this.endMessages && !this.startMessages) {
			this.setMessagesLoaded(true);
			return;
		}

		// Reset rendered window AND the Firestore pagination cursors when
		// switching rooms. Without the cursor reset, _fetchMessages would do
		// startAfter(<doc-from-previous-room>) against the new room's messages
		// subcollection — Firestore behavior with cross-collection cursors is
		// undefined / can return empty pages.
		if (this.selectedRoom !== room.roomId) {
			this.renderedMessageSize = 25;
			this.$store.commit('chat/setStartMessages', null);
			this.$store.commit('chat/setEndMessages', null);
			this.$store.commit('chat/setMessagesLoaded', false);
		}
		this.selectedRoom = room.roomId;

		// If we have older messages already in Vuex above the rendered window,
		// expand the window first instead of hitting Firestore. The library fires
		// fetch-messages on scroll-to-top; we want that to reveal already-loaded
		// older messages before going back to the network.
		if (this.messages.length > this.renderedMessageSize) {
			this.renderedMessageSize = Math.min(
				this.messages.length,
				this.renderedMessageSize + 50
			);
			return;
		}

		let payload = {
			room: room,
			messagesPerPage: this.messagesPerPage,
			startMessages: this.startMessages,
		}

		await this._fetchMessages(payload);

		// After fetching from Firestore, ensure the newly loaded older messages
		// are inside the rendered window (otherwise the user scrolled up and saw
		// nothing change).
		if (this.messages.length > this.renderedMessageSize) {
			this.renderedMessageSize = this.messages.length;
		}

		// Render recent emoji row when room messages are loaded
		this.$nextTick(() => this.renderRecentEmojiRow());

		if (this.selectedRoom !== room.roomId)
			return;

		//this.listeners.push(listener);
	},
    
	async sendMessage({ content, roomId, file, files, replyMessage }) {
		let currentRoomUsers = this.rooms.filter(room => room.roomId === roomId)[0].users;
		let currentRoomUserIds = currentRoomUsers.filter(user => user.employeeID !== this.userID).map(user => user.employeeID);

		// Normalize: library emits single `file`, our paste/drop sends `files` array
		const fileList = files || (file ? [file] : []);
		const batchId = fileList.length > 1 ? uuidv4() : null;

		if (fileList.length > 0) {
			for (let i = 0; i < fileList.length; i++) {
				const f = fileList[i];

				// Reconstruct full filename (library strips extension from name)
				const fullName = f.extension ? `${f.name}.${f.extension}` : f.name;

				// Validate — create a temporary File object for the validator
				const fileForValidation = new File([f.blob || f], fullName, { type: f.type });
				const validation = validateFile(fileForValidation);
				if (!validation.valid) {
					this.$toast.error(`${fullName}: ${validation.error}`);
					continue;
				}

				const message = {
					sender_id: this.currentUserId,
					content: i === 0 ? content : '', // Caption on first message only
					timestamp: new Date(),
					expireAt: getChatExpireAt(),
					notseen: currentRoomUserIds,
					file: {
						name: fullName,
						size: f.size,
						type: f.type,
						extension: f.extension || '',
						url: f.localUrl || ''
					},
					fileCategory: getFileCategory(f.type),
					batchId: batchId
				};
				if (f.audio) {
					message.file.audio = true;
					message.file.duration = f.duration;
				}
				if (i === 0 && replyMessage) {
					message.replyMessage = {
						_id: replyMessage._id,
						content: replyMessage.content,
						sender_id: replyMessage.sender_id,
						notseen: currentRoomUserIds
					};
					if (replyMessage.file) {
						message.replyMessage.file = replyMessage.file;
					}
				}
				const { id } = await this._sendMessage({ roomId, message });
				// Upload the actual file blob to S3
				this.uploadFile({
					file: { ...f, name: fullName, blob: f.blob || fileForValidation },
					messageId: id,
					roomId
				});
			}
		} else {
			// Text-only message
			const message = {
				sender_id: this.currentUserId,
				content,
				timestamp: new Date(),
				expireAt: getChatExpireAt(),
				notseen: currentRoomUserIds
			};
			if (replyMessage) {
				message.replyMessage = {
					_id: replyMessage._id,
					content: replyMessage.content,
					sender_id: replyMessage.sender_id,
					notseen: currentRoomUserIds
				};
				if (replyMessage.file) {
					message.replyMessage.file = replyMessage.file;
				}
			}
			await this._sendMessage({ roomId, message });
		}

		// Track emoji usage for recent emojis row
		this.trackEmojiUsage(content);
		this.$nextTick(() => this.renderRecentEmojiRow());
	},

	async editMessage({ messageId, newContent, roomId, file }) {
		await this._editMessage({
			roomId, messageId, newContent, file
		});

		if (file) this.uploadFile({ file, messageId, roomId });
	},

	async deleteMessage({ messageId, roomId }) {
		// Look up the full message to check for file attachments
		const message = this.messages.find(m => m._id === messageId);

		// Delete S3 file if message has one
		if (message && message.file && message.file.url && message.file.url.includes('s3.amazonaws.com')) {
			try {
				const url = new URL(message.file.url);
				const key = decodeURIComponent(url.pathname.substring(1)); // Remove leading /
				await deleteFromS3({
					bucket: 'teamio-clients-prod',
					keys: [key]
				});
			} catch (err) {
				console.error('Failed to delete S3 file:', err);
				// Continue with message deletion even if S3 delete fails
			}
		}
		await this._deleteMessage({ messageId, roomId });
	},

	menuActionHandler({ action, roomId }) {
		switch (action.name) {
			case 'inviteUser':
				return this.inviteUser(roomId)
			case 'removeUser':
				return this.removeUser(roomId)
			case 'renameRoom':
				return this.openRenameRoomModal(roomId)
			case 'deleteRoom':
				this.deleteRoomInProgess = true;
				this.showModal('sure-modal-remove-room', roomId);
				break;
			case 'exportChat':
				return this.openPerRoomExport(roomId);
		}
    },

	showModal(modal, roomId) {
		this.removeRoomId = roomId;
    	this.$refs[modal].show();
    },

	hideModal(ref) {
      this.$refs[ref].hide()
    },

	openRenameRoomModal(roomId) {
		const room = this.rooms.find(r => r.roomId === roomId);
		if (room) {
			this.roomToRenameId = roomId;
			this.roomToRenameCurrentName = room.roomName;
			this.newCustomNameForEdit = room.roomName;
			this.showRenameModal = true;
		}
	},

	async submitRenameRoom() {
		const newName = this.newCustomNameForEdit.trim();
		if (!newName) {
			this.$toast.error('Room name cannot be empty.');
			return;
		}
		if (newName === this.roomToRenameCurrentName) {
			this.showRenameModal = false;
			return;
		}
		// Uniqueness check (exclude current room)
		const exists = this.rooms.some(r => r.roomName === newName && r.roomId !== this.roomToRenameId);
		if (exists) {
			this.$toast.error('A room with this name already exists.');
			return;
		}
		this.busy = true;
		try {
			await this.$store.dispatch('chat/_updateRoomName', { roomId: this.roomToRenameId, newName });
			this.showRenameModal = false;
			this.roomToRenameId = null;
			this.roomToRenameCurrentName = '';
			this.newCustomNameForEdit = '';
		} catch (e) {
			this.$toast.error('Failed to rename room.');
		} finally {
			this.busy = false;
		}
	},
	
	openFile({ message }) {
		if (!message.file || !message.file.url) return;
		const category = getFileCategory(message.file.type);
		if (category === 'image') {
			// Open lightbox for images
			const allImages = this.messages
				.filter(m => m.file && !m.deleted && getFileCategory(m.file.type) === 'image' && m.file.url)
				.map(m => ({ url: m.file.url, name: m.file.name }));
			const idx = allImages.findIndex(img => img.url === message.file.url);
			this.lightboxImages = allImages;
			this.lightboxStartIndex = idx >= 0 ? idx : 0;
			this.lightboxVisible = true;
		} else {
			// Open non-image files in new tab
			window.open(message.file.url, '_blank');
		}
	},

	async uploadFile({ file, messageId, roomId }) {
		try {
			// Convert blob to File object if needed
			const fileObj = file.blob instanceof File
				? file.blob
				: new File([file.blob], file.name, { type: file.type });

			const result = await uploadToS3({
				file: fileObj,
				siteID: window.localStorage.teamioSiteID,
				teamioType: 'CHAT',
				roomId: roomId,
				messageId: messageId
			});
			// Update ONLY the file URL in Firestore (don't touch content or other fields)
			await this.$store.dispatch('chat/_updateMessageFileUrl', {
				roomId,
				messageId,
				url: result.url
			});
		} catch (err) {
			console.error('Chat file upload failed:', err);
			this.$toast.error('File upload failed. Please try again.');
		}
	},

	insertRecentEmoji(emoji) {
		const textarea = document.querySelector('.vac-box-footer textarea');
		if (textarea) {
			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const text = textarea.value;
			textarea.value = text.substring(0, start) + emoji + text.substring(end);
			textarea.dispatchEvent(new Event('input', { bubbles: true }));
			// Set cursor after the emoji
			const newPos = start + emoji.length;
			textarea.setSelectionRange(newPos, newPos);
			textarea.focus();
		}
		this.trackSingleEmoji(emoji);
	},

	trackSingleEmoji(emoji) {
		let recents = [...this.recentEmojis];
		recents = recents.filter(e => e !== emoji);
		recents.unshift(emoji);
		recents = recents.slice(0, 10);
		this.recentEmojis = recents;
		localStorage.setItem('teamio-recent-emojis', JSON.stringify(recents));
		this.renderRecentEmojiRow();
	},

	trackEmojiUsage(content) {
		if (!content) return;
		// Extract emojis from the message
		const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu;
		const found = content.match(emojiRegex);
		if (!found || found.length === 0) return;

		let recents = [...this.recentEmojis];
		found.forEach(emoji => {
			// Move to front, dedup
			recents = recents.filter(e => e !== emoji);
			recents.unshift(emoji);
		});
		// Keep max 10
		recents = recents.slice(0, 10);
		this.recentEmojis = recents;
		localStorage.setItem('teamio-recent-emojis', JSON.stringify(recents));
	},

	renderRecentEmojiRow() {
		// Remove existing row if any
		if (this.recentEmojiEl) {
			this.recentEmojiEl.remove();
			this.recentEmojiEl = null;
		}

		const footer = document.querySelector('.vac-box-footer');
		if (!footer) return;

		const row = document.createElement('div');
		row.className = 'recent-emoji-row';

		// Header label
		const header = document.createElement('span');
		header.className = 'recent-emoji-header';
		header.textContent = 'Quick Actions';
		header.title = 'Recently used emojis — click any to insert';
		row.appendChild(header);

		this.recentEmojis.forEach(emoji => {
			const btn = document.createElement('span');
			btn.textContent = emoji;
			btn.className = 'recent-emoji-btn';
			btn.addEventListener('click', () => this.insertRecentEmoji(emoji));
			row.appendChild(btn);
		});

		footer.parentElement.insertBefore(row, footer);
		this.recentEmojiEl = row;

		// Add tooltip to "+" add room icon
		const addRoomIcon = document.querySelector('.vac-svg-button.vac-add-icon');
		if (addRoomIcon && !addRoomIcon.getAttribute('title')) {
			addRoomIcon.setAttribute('title', 'Create New Room');
		}

		// Add tooltip to attachment icon (paperclip)
		const attachIcon = footer.querySelector('.vac-svg-button[id*="paperclip"], .vac-icon-textarea-left + .vac-icon-textarea, .vac-svg-button');
		if (attachIcon && !attachIcon.getAttribute('title')) {
			// Find the paperclip icon specifically (second icon in the right icon group)
			const rightIcons = footer.querySelectorAll('.vac-icon-textarea svg');
			rightIcons.forEach(svg => {
				const path = svg.querySelector('path');
				if (path && path.getAttribute('d') && path.getAttribute('d').includes('16.5,6V17.5')) {
					// This is the paperclip/attachment icon
					svg.parentElement.setAttribute('title', `Attach file — Allowed: ${getAcceptedFileTypesDescription()}. Max: 10MB images, 25MB docs, 50MB video/audio. Files retained for 90 days.`);
				}
			});
		}
	},

	messageActionHandler() {
		// do something
	},

	textareaActionHandler({ message, roomId }) {
		this.roomMessage = 'Implement your own action!';
	},

	async sendMessageReaction({ reaction, remove, messageId, roomId }) {
		try {
			await this.$store.dispatch('chat/sendMessageReaction', { reaction, remove, messageId, roomId });
			if (!remove && reaction && reaction.unicode) {
				this.trackSingleEmoji(reaction.unicode);
			}
		} catch (err) {
			console.error('Reaction failed to save', err);
		}
	},

	// Add a new method to load rooms with timeout
	loadRooms() {
		setTimeout(() => {
			this.fetchRooms()
				.then(() => {
					this.$nextTick(() => {
						this.busy = false;
						// Force UI update
						this.$forceUpdate();

						setTimeout(() => {
							// Double check busy state
							if (this.busy) {
								this.busy = false;
								this.$forceUpdate();
							}
						}, 500);
					});
				})
				.catch(error => {
					console.error('Error in loadRooms:', error);
					this.$nextTick(() => {
						this.busy = false;
						this.$forceUpdate();
					});
				});
		}, 500);
	},
  },

  computed: {
    // Demo site (plan Task 6.5): team chat export is not part of the demo (the server refuses it too).
    isDemoSession() {
      return !!this.$store && this.$store.getters['customer/isDemo'] === true;
    },
    // Fallback in case prop was not bound: use Vuex userID
    isEmbedded() {
      return this.mode === 'embedded';
    },
    // ─── Team Chat Export — context for the modal ──────────────────────
    userSiteId() {
      return this.siteID;
    },
    userSiteName() {
      const site = (typeof this.getSite === 'function') ? this.getSite(this.siteID) : null;
      return (site && site.Name) || null;
    },
    userEmail() {
      const u = this.user || {};
      if (u.Email || u.email) return u.Email || u.email;
      // Federated (Microsoft/Google) logins store the address in Username; some
      // legacy accounts use a non-email handle there, so only use it when it's
      // actually email-shaped. Otherwise return '' so the export modal shows the
      // fallback and keeps Start Export disabled rather than emailing a bad address.
      const uname = u.Username || u.username || '';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(uname) ? uname : '';
    },
    userName() {
      if (!this.user) return '';
      const first = this.user.FirstName || this.user.firstName || '';
      const last = this.user.LastName || this.user.lastName || '';
      const full = `${first} ${last}`.trim();
      return full || this.user.Username || this.user.username || '';
    },
    userTimezone() {
      const tzSetting = this.settings && this.settings.GENERAL_TIMEZONE;
      return (tzSetting && tzSetting.PropertyValue) || null;
    },
    effectiveCurrentUserId() {
      return this.currentUserId || this.userID;
    },
    acceptedFileTypes() {
      return getAcceptedFileTypes();
    },
	...mapState({
         rooms: state => state.chat.rooms,
		 startRooms: state => state.chat.startRooms,
		 endRooms: state => state.chat.endRooms,
		 roomsLoaded: state => state.chat.roomsLoaded,
		 loadingRooms: state => state.chat.loadingRooms,
		 allUsers: state => state.chat.allUsers,
		 messages: state => state.chat.messages,
		 messagesLoaded: state => state.chat.messagesLoaded,
		 roomsListener: state => state.chat.roomsListener,
		 listeners: state => state.chat.listeners,
		 roomsLoadedCount: state => state.chat.roomsLoadedCount,
		 startMessages: state => state.chat.startMessages,
		 endMessages: state => state.chat.endMessages,
	}),
	unreadRoomCount() {
		if (!this.rooms) return 0;
		return this.rooms.filter(r => (parseInt(r.unreadCount) || 0) > 0).length;
	},
	visibleRooms() {
		if (this.activeFilter !== 'unread' || !this.rooms) return this.rooms;
		return this.rooms.filter(r =>
			(parseInt(r.unreadCount) || 0) > 0 || r.roomId === this.selectedRoom
		);
	},

	menuActions() {
		let menuActions;

		if(this.$can('delete', 'chatroom')) {
			menuActions = [
				{ name: 'inviteUser', title: 'Invite User' },
				{ name: 'removeUser', title: 'Remove User' },
				{ name: 'renameRoom', title: 'Rename Room' },
				{ name: 'deleteRoom', title: 'Delete Room' }
			]
		} else {
			menuActions = [
				{ name: 'inviteUser', title: 'Invite User' },
				{ name: 'renameRoom', title: 'Rename Room' }
			]
		}
		// Team Chat Export — admin-only. Separable from delete so a future
		// "Office Manager can export but not delete" role works without code change.
		if (this.$can('export', 'chatroom') && !this.isDemoSession) {
			menuActions.push({ name: 'exportChat', title: 'Export Chat' });
		}
		return menuActions;
	},
	  
    loadedRooms() {
		return this.rooms.slice(0, this.roomsLoadedCount);
	},

	employeesToChatWith() {
		if(this.activeEmployees)
			return this.activeEmployees.filter(e => e.EmployeeID !== this.userID);
	},

	windowedMessages() {
		// Cap rendered message count to avoid the per-keystroke render cascade
		// in vue-advanced-chat (every Message child re-renders on every keystroke
		// because scoped-slot fan-out defeats Vue 2's child-render skip).
		// New arrivals stay visible because we slice from the end. Older history
		// becomes accessible by scrolling up — fetchMessages grows the window then.
		if (this.messages.length <= this.renderedMessageSize) return this.messages;
		return this.messages.slice(this.messages.length - this.renderedMessageSize);
	},

  },

  watch: {
	  rooms(newValue, oldValue) {
		//console.log(`messages changed. New value: ${JSON.stringify(newValue)}, Old value: ${JSON.stringify(oldValue)}`);
		//console.dir(newValue);
		//console.dir(oldValue);
        if(!oldValue && !newValue.empty) {
			if (this.loadingLastMessageByRoom < this.rooms.length) {
				this.loadingLastMessageByRoom++;
				if (this.loadingLastMessageByRoom === this.rooms.length) {
					this.loadingRooms = false;
					this.setRoomsLoadedCount(this.rooms.length);
				}
            }
        }
      },
      
      busy(newValue) {
        // Force a UI update whenever the busy state changes
        this.$forceUpdate();
      },

      messages(newVal, oldVal) {
        // Auto-scroll to bottom when messages are loaded or a new message arrives
        // The library's internal scroll uses setTimeout(0) which fires before Vue renders,
        // so we use $nextTick + a small delay to ensure DOM is updated first
        if (newVal && newVal.length) {
          this.$nextTick(() => {
            setTimeout(() => {
              const el = document.querySelector('.vac-container-scroll');
              if (el) {
                // Only auto-scroll if user is near the bottom (within 200px) or this is initial load
                const isNearBottom = el.scrollHeight - el.clientHeight - el.scrollTop < 200;
                const isInitialLoad = !oldVal || oldVal.length === 0;
                if (isNearBottom || isInitialLoad) {
                  el.scrollTo({ top: el.scrollHeight });
                }
              }
            }, 100);
          });
        }
      }
  }
}

</script>

<style lang="scss" scoped>
$brand-green: #3abf94;

/* Add keyframes for spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// =====================================================
// CHAT PAGE HEADER - Modern styling matching other pages
// =====================================================
.chat-page {
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	// Force chat window to fill available height (library sets inline height we must override)
	&:not(.chat-page--embedded) > .vac-card-window {
		height: 100% !important;
		flex: 1 !important;
		min-height: 0 !important;
	}
}

// Embedded mode (PracticePulse, etc.) — compact layout
.chat-page--embedded {
	.chat-page-header { display: none; }

	:deep(.vac-card-window) {
		height: 400px !important;
	}
}

.chat-page-header {
	display: flex;
	align-items: center;
	gap: 16px;
	margin-bottom: 24px;
	padding-bottom: 20px;
	border-bottom: 1px solid #e2e8f0;

	.header-icon-wrapper {
		width: 52px;
		height: 52px;
		border-radius: 14px;
		background: linear-gradient(135deg, $brand-green 0%, darken($brand-green, 10%) 100%);
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 4px 14px rgba(58, 191, 148, 0.3);
		flex-shrink: 0;

		i {
			font-size: 1.5rem;
			color: #fff;
		}
	}

	.header-titles {
		flex: 1;

		h1 {
			font-size: 1.625rem;
			font-weight: 700;
			color: #1e293b;
			margin: 0 0 4px 0;
			line-height: 1.2;
		}

		p {
			font-size: 0.875rem;
			color: #64748b;
			margin: 0;
		}
	}

	.new-chat-btn {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 10px 20px;
		background: $brand-green;
		color: #fff;
		border: none;
		border-radius: 8px;
		font-size: 0.875rem;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.2s ease;
		box-shadow: 0 2px 8px rgba(58, 191, 148, 0.25);

		i {
			font-size: 0.875rem;
		}

		&:hover {
			background: darken($brand-green, 6%);
			box-shadow: 0 4px 12px rgba(58, 191, 148, 0.35);
			transform: translateY(-1px);
		}

		&:active {
			transform: translateY(0);
		}
	}

	.chat-settings-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 40px;
		height: 40px;
		margin-left: 8px;
		margin-right: 4px;
		padding: 0;
		border-radius: 8px;
		background: white;
		border: 1px solid #e2e8f0;
		color: #718096;
		cursor: pointer;
		transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

		&:hover {
			background: #f7fafc;
			color: $brand-green;
			border-color: $brand-green;
		}

		.fa {
			font-size: 14px;
			transition: transform 0.3s ease;
		}

		&:hover .fa {
			transform: rotate(45deg);
		}
	}

	// Admin-only ⋮ menu on the header (Export All Team Chats)
	.chat-admin-menu {
		margin-left: 4px;

		::v-deep .chat-admin-menu-toggle {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 40px;
			height: 40px;
			padding: 0;
			border-radius: 8px;
			background: white;
			border: 1px solid #e2e8f0;
			color: #718096;
			transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

			&:hover {
				background: #f7fafc;
				color: $brand-green;
				border-color: $brand-green;
				text-decoration: none;
			}
		}
	}

	// Phone-class viewports — the single-row header (icon + titles +
	// New Conversation + Settings gear) doesn't fit in ~326px of chat-page
	// width. The settings gear got pushed off the right edge entirely on
	// Keep on Smiling iPhone 2026-05-05. Two changes here:
	//  - flex-wrap so the action buttons (New Conversation + gear) drop to
	//    a second row when row 1 is full. The icon + title share row 1.
	//  - hide the subtitle paragraph ("Internal messaging for your team
	//    members") on phones — it eats vertical space without telling the
	//    user anything they don't already know from the page they're on.
	@media (max-width: 768px) {
		flex-wrap: wrap;
		gap: 10px;
		margin-bottom: 16px;
		padding-bottom: 16px;

		.header-icon-wrapper {
			width: 40px;
			height: 40px;
			border-radius: 10px;

			i { font-size: 1.125rem; }
		}

		.header-titles {
			flex: 1 1 0;
			min-width: 0; // allow shrink

			h1 {
				font-size: 1.25rem;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
			p { display: none; }
		}

		// Action buttons share row 2 — primary CTA grows, gear stays
		// its 40px width and stays reachable.
		.new-chat-btn {
			flex: 1 1 0;
			justify-content: center;
			padding: 10px 12px;
		}

		.chat-settings-btn {
			margin-left: 0;
			margin-right: 0;
		}
	}
}

.window-container {
	width: 100%;
}

// =====================================================
// CHAT FORMS - Modern styling (SCOPED for proper specificity)
// =====================================================
.chat-forms {
	padding: 20px 24px;
	background: #f8fafc;
	border-radius: 12px;
	margin-bottom: 16px;
	border: 1px solid #e2e8f0;

	// AGGRESSIVE: Remove all possible borders and gray bars from all elements
	:deep(*),
	:deep(*::before),
	:deep(*::after) {
		border-top: none !important;
		border-bottom-color: transparent !important;
	}

	form {
		display: flex;
		flex-direction: column;
		gap: 16px;
		padding: 0 !important;
		margin: 0 !important;
		background: transparent !important;
		border: none !important;
		box-shadow: none !important;
	}

	h3 {
		font-size: 1.0625rem;
		font-weight: 600;
		color: #1e293b;
		margin: 0 0 4px 0;
		padding: 0 !important;
		border: none !important;
		background: transparent !important;
		box-shadow: none !important;
	}

	// Form row for inputs and buttons in same line
	.form-row {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
		margin: 0 !important;
		padding: 0 !important;
		border: none !important;
		background: transparent !important;
	}

	input {
		padding: 10px 14px;
		height: 42px;
		border-radius: 8px;
		border: 1px solid #e2e8f0 !important;
		outline: none;
		font-size: 0.875rem;
		background: #fff;
		transition: border-color 0.2s, box-shadow 0.2s;

		&:focus {
			border-color: $brand-green !important;
			box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
		}

		&::placeholder {
			color: #94a3b8;
		}
	}

	.chat-new-room-name {
		width: 220px;
		max-width: 100%; // shrink on narrow viewports instead of overflowing
		flex-shrink: 1;
	}

	// Button row - for when buttons need their own line
	.button-row {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	button {
		background: $brand-green;
		color: #fff;
		outline: none;
		cursor: pointer;
		border-radius: 8px;
		padding: 10px 20px;
		border: none !important;
		font-size: 0.875rem;
		font-weight: 600;
		transition: all 0.2s ease;
		height: 42px;
		white-space: nowrap;
		flex-shrink: 0;

		&:hover {
			background: darken($brand-green, 6%);
		}

		&:active {
			opacity: 0.9;
		}

		&:disabled {
			cursor: not-allowed;
			background: #cbd5e1;
			opacity: 0.6;
		}
	}

	.button-cancel {
		color: #64748b;
		background: #fff;
		border: 1px solid #e2e8f0 !important;

		&:hover {
			color: #334155;
			background: #f1f5f9;
			border-color: #cbd5e1 !important;
		}
	}

	select {
		height: 42px;
		width: 120px;
		font-size: 0.875rem;
		border-radius: 8px;
		border: 1px solid #e2e8f0 !important;
		background: #fff;
		padding: 0 12px;
	}
}

// =====================================================
// MULTISELECT STYLING - Using :deep() for child component
// =====================================================
.chat-forms :deep(.multiselect) {
	width: 320px;
	// Allow shrink-to-fit on narrow viewports (chat-page is ~326px on
	// iPhone Pro Max with sidebar visible — 320px multiselect + 24px
	// of .chat-forms padding meant the multiselect overflowed and the
	// "Select team members" placeholder was visually clipped). With
	// max-width:100% and flex-shrink:1 the field collapses to fit; on
	// desktop the 320px preferred width still applies because there's
	// room. Keep on Smiling 2026-05-05.
	max-width: 100%;
	flex-shrink: 1;
	background: transparent !important;
	border: none !important;
	box-shadow: none !important;

	.multiselect__tags {
		border: 1px solid #e2e8f0 !important;
		border-radius: 8px;
		padding: 6px 40px 6px 12px;
		min-height: 42px;
		background: #fff !important;
		box-shadow: none !important;
	}

	.multiselect__select {
		background: transparent !important;
		height: 40px;
		width: 36px;
		border: none !important;

		&::before {
			border-color: #94a3b8 transparent transparent transparent;
		}
	}

	.multiselect__spinner {
		background: transparent !important;
		border: none !important;
	}

	.multiselect__tag {
		background-color: $brand-green !important;
		color: #fff;
		border-radius: 6px;
		padding: 5px 26px 5px 10px;
		font-size: 0.8125rem;
		font-weight: 500;
		border: none !important;
	}

	.multiselect__placeholder {
		color: #94a3b8;
		padding-top: 2px;
	}

	.multiselect__option--highlight {
		background: $brand-green !important;
	}

	.multiselect__input {
		background: transparent !important;
		border: none !important;
	}

	.multiselect__content-wrapper {
		background: #fff !important;
		border: 1px solid #e2e8f0 !important;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}
}

// Scrollbar styling for chat window (using :deep for scoped)
:deep(.vac-rooms-container),
:deep(.vac-messages-container) {
	&::-webkit-scrollbar {
		width: 4px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: #e2e8f0;
		border-radius: 2px;

		&:hover {
			background: #cbd5e1;
		}
	}
}

.chat-retention-notice {
	text-align: center;
	font-size: 11px;
	color: #94a3b8;
	padding: 6px 12px;
	line-height: 1.4;

	.fa-info-circle {
		margin-right: 4px;
	}
}

// Chat-page-scoped copyright line. Lives in flow at the bottom of the chat
// page (right after .chat-retention-notice) instead of relying on the
// global .contentFooter — that one is position:absolute at bottom:15px of
// .content, which collided with the in-flow retention notice on phones
// (Keep on Smiling 2026-05-05). The global footer is suppressed via
// Layout.scss `.content:has(.chat-page) .contentFooter { display: none }`,
// and this in-flow line replaces it. Lighter color than the retention
// notice so the eye reads retention first.
.chat-page-copyright {
	text-align: center;
	font-size: 10px;
	color: #cbd5e1;
	padding: 0 12px 8px;
	line-height: 1;
}

// Team Chat Export — status pill in the header strip when a job is pending
.chat-export-status-pill {
	display: inline-flex;
	align-items: center;
	margin: 0 auto 12px;
	padding: 6px 14px;
	background: #ECFDF5;
	border: 1px solid #A7F3D0;
	color: #065F46;
	border-radius: 16px;
	font-size: 13px;
	max-width: 520px;
	align-self: center;

	.close-pill-btn {
		background: transparent;
		border: 0;
		color: #065F46;
		font-size: 18px;
		line-height: 1;
		padding: 0 4px;
		cursor: pointer;
		opacity: 0.6;

		&:hover { opacity: 1; }
	}
}
</style>

<!-- Unscoped styles for vue-advanced-chat component overrides -->
<style lang="scss">
// These styles MUST be unscoped to override vue-advanced-chat internal styles
$brand-green: #3abf94;
$room-avatar-color: #3b82f6;

// =====================================================
// CHAT WINDOW CONTAINER - Clean, modern design
// =====================================================
.vac-card-window {
	border-radius: 12px !important;
	border: 1px solid #e2e8f0 !important;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
	background: #fff !important;
	overflow: hidden !important;

	& > div {
		border: none !important;
	}
}

// =====================================================
// ROOM LIST STYLING - Better visibility for room items
// =====================================================
.vac-rooms-container {
	background: #f8fafc !important;
	padding: 8px 0 !important;
	border-right: 1px solid #f1f5f9 !important;

	.vac-room-list {
		padding: 0 !important;
	}
}

// Remove default vue-advanced-chat room dividers/borders
.vac-room-divider,
.vac-room-border,
.vac-line-room {
	display: none !important;
	border: none !important;
	height: 0 !important;
}

.vac-room-item {
	background: #fff !important;
	border: 1px solid #e2e8f0 !important;
	border-radius: 10px !important;
	margin: 0 10px 12px 10px !important;
	padding: 14px 12px !important;
	transition: all 0.15s ease !important;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;

	&:hover {
		border-color: #cbd5e1 !important;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
		transform: translateY(-1px);
		// Same as ConversationContainerV2: the transform traps a row menu under the next row unless the row is lifted.
		z-index: 2;
	}

	&::before,
	&::after {
		display: none !important;
		border: none !important;
		content: none !important;
	}

	.vac-room-container,
	.vac-name-container,
	.vac-text-ellipsis {
		border: none !important;
		border-bottom: none !important;
	}
}

// Override any border-bottom that creates gray lines
[class*="vac-room"] {
	border-bottom: none !important;
}

// Re-apply our intentional border to room-item only
.vac-room-item {
	border: 1px solid #e2e8f0 !important;
}

.vac-room-item.vac-room-selected {
	background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%) !important;
	border: 1px solid #86efac !important;
	border-left: 3px solid $brand-green !important;
	box-shadow: 0 2px 8px rgba(58, 191, 148, 0.2) !important;
}

.vac-room-name {
	font-weight: 600 !important;
	color: #1e293b !important;
	font-size: 0.9rem !important;
}

.vac-text-last {
	color: #64748b !important;
	font-size: 0.8rem !important;
}

.vac-text-date {
	color: #94a3b8 !important;
	font-size: 0.75rem !important;
}

// =====================================================
// ROOM AVATAR STYLING
// =====================================================
.vac-avatar,
.vac-room-avatar {
	width: 44px !important;
	height: 44px !important;
	min-width: 44px !important;
	min-height: 44px !important;
	border-radius: 12px !important;
	background: linear-gradient(135deg, lighten($room-avatar-color, 5%) 0%, darken($room-avatar-color, 8%) 100%) !important;
	margin-right: 14px !important;
	box-shadow: 0 3px 10px rgba(59, 130, 246, 0.25) !important;
	border: none !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	flex-shrink: 0 !important;
	position: relative !important;
	overflow: visible !important;
	font-size: 0 !important;
	color: transparent !important;

	&::before {
		content: '\f0c0' !important;
		font-family: 'FontAwesome' !important;
		font-size: 1rem !important;
		color: #fff !important;
		position: absolute !important;
		top: 50% !important;
		left: 50% !important;
		transform: translate(-50%, -50%) !important;
	}

	&::after {
		content: '' !important;
		position: absolute !important;
		bottom: -2px !important;
		right: -2px !important;
		width: 12px !important;
		height: 12px !important;
		background: $brand-green !important;
		border-radius: 50% !important;
		border: 2px solid #fff !important;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15) !important;
	}
}

.vac-room-header .vac-avatar,
.vac-info-wrapper .vac-avatar,
.vac-room-header .vac-room-avatar,
.vac-info-wrapper .vac-room-avatar {
	width: 38px !important;
	height: 38px !important;
	min-width: 38px !important;
	min-height: 38px !important;
	border-radius: 10px !important;
	margin-right: 12px !important;

	&::before {
		font-size: 0.875rem !important;
	}

	&::after {
		width: 10px !important;
		height: 10px !important;
		bottom: -1px !important;
		right: -1px !important;
	}
}

// =====================================================
// CHAT HEADER
// =====================================================
.vac-room-header {
	background: #f8fafc !important;
	padding: 12px 16px !important;
	border-bottom: 1px solid #e2e8f0 !important;

	.vac-room-name {
		color: #334155 !important;
		font-weight: 600 !important;
		font-size: 0.875rem !important;
	}

	.vac-icon-add {
		background: transparent !important;
		border-radius: 6px !important;
		padding: 4px !important;

		svg {
			fill: $brand-green !important;
		}

		&:hover {
			background: rgba($brand-green, 0.1) !important;
		}
	}
}

// =====================================================
// MESSAGES AREA
// =====================================================
.vac-messages-container {
	background: #fff !important;
	flex: 1 !important;
	min-height: 0 !important;
}

.vac-col-messages {
	background: #fff !important;
	flex: 1 !important;
	display: flex !important;
	flex-direction: column !important;
	border: none !important;
}

.vac-container-scroll {
	background: #fff !important;
	flex: 1 !important;
	display: flex !important;
	flex-direction: column !important;
}

// =====================================================
// MESSAGE BUBBLE STYLING
// =====================================================
.vac-message-card {
	background-color: #e0f2fe !important;
	border: 1px solid #bae6fd !important;
	border-radius: 12px !important;
}

.vac-message-card.vac-message-current {
	background-color: #dcfce7 !important;
	border: 1px solid #86efac !important;
}

// =====================================================
// EMOJI REACTION BADGE — bottom-right of bubble
// vue-advanced-chat renders .vac-button-reaction inside
// a classless <span> (no .vac-message-reactions class exists)
// =====================================================
.vac-message-container {
	position: relative !important;
	padding-bottom: 10px !important;
}
// Push timestamp up so badge doesn't obscure it
.vac-message-card {
	padding-bottom: 18px !important;
}
.vac-message-container .vac-button-reaction {
	position: absolute !important;
	bottom: 0 !important;
	top: auto !important;
	right: -16px !important;
	left: auto !important;
	z-index: 2;
	font-size: 12px !important;
	line-height: 16px !important;
	padding: 1px 4px !important;
	border-radius: 10px !important;
	box-shadow: 0 1px 4px rgba(0,0,0,0.18) !important;
}
// Allow badge and emoji picker to overflow the message column
.vac-col-messages {
	overflow: visible !important;
}
// =====================================================
// HIDE OPTIONS BUTTON UNTIL HOVER (the grey circle)
// =====================================================
.vac-message-container .vac-options-container {
	opacity: 0 !important;
	visibility: hidden !important;
	overflow: visible !important;
	transition: opacity 0.15s, visibility 0.15s;
}
.vac-message-container:hover .vac-options-container {
	opacity: 1 !important;
	visibility: visible !important;
}

.vac-text-username {
	color: $brand-green !important;
	font-weight: 500 !important;
	font-size: 0.75rem !important;
}

.vac-text-content {
	color: #334155 !important;
	font-size: 0.875rem !important;
}

.vac-text-timestamp {
	color: #64748b !important;
	font-size: 0.6875rem !important;
}

// =====================================================
// MESSAGE INPUT
// =====================================================
// Flexbox min-content overflow guard. vue-advanced-chat puts the textarea
// inside a deep flex chain: .vac-col-messages → .vac-room-footer →
// .vac-box-footer → textarea. Each level defaults to `min-width: auto`,
// which prevents flex items from shrinking below their content's intrinsic
// min-width. The textarea's intrinsic width (~414px) bubbled up through
// the chain and pushed the column to 583px wide on a 430px-viewport iPhone,
// clipping the send button 150+px past the right edge of the chat card.
// Setting min-width:0 on the flex chain lets the column collapse to fit
// the chat-page width. Keep on Smiling Dentistry iPhone screenshot,
// 2026-05-05. Applies at all viewports — flex shrinkage is fine on
// desktop too, the constraint only kicks in when there's pressure.
.vac-col-messages,
.vac-row,
.vac-chat-container {
	min-width: 0 !important;
}
.vac-room-footer {
	background: #f8fafc !important;
	border-top: 1px solid #e2e8f0 !important;
	padding: 12px 16px !important;
	min-width: 0 !important;

	.vac-box-footer {
		background: #fff !important;
		border: 1px solid #e2e8f0 !important;
		border-radius: 10px !important;
		padding: 8px 12px !important;
		min-width: 0 !important;

		&:focus-within {
			border-color: $brand-green !important;
			box-shadow: 0 0 0 2px rgba(58, 191, 148, 0.1) !important;
		}
	}

	textarea {
		font-size: 0.875rem !important;
		color: #334155 !important;

		&::placeholder {
			color: #94a3b8 !important;
		}
	}

	.vac-icon-textarea svg {
		fill: $brand-green !important;
	}

	.vac-send-disabled svg {
		fill: #cbd5e1 !important;
	}
}

// Hide default add room button
.vac-icon-add-room {
	display: none !important;
}

// =====================================================
// MULTISELECT OVERRIDE STYLES (UNSCOPED)
// =====================================================
.chat-page .chat-forms {
	*,
	*::before,
	*::after {
		border-top: none !important;
		border-bottom: none !important;
	}

	// Re-apply specific borders we actually want
	border: 1px solid #e2e8f0 !important;

	.multiselect__tags {
		border: 1px solid #e2e8f0 !important;
	}

	.button-cancel {
		border: 1px solid #e2e8f0 !important;
	}

	input[type="text"] {
		border: 1px solid #e2e8f0 !important;
	}
}

.chat-page .multiselect {
	background: transparent !important;
	border: none !important;
	box-shadow: none !important;
}

.chat-page .multiselect__tags {
	border: 1px solid #e2e8f0 !important;
	border-radius: 8px !important;
	background: #fff !important;
	box-shadow: none !important;
}

.chat-page .multiselect__select {
	background: transparent !important;
	border: none !important;
}

.chat-page .multiselect__spinner {
	background: transparent !important;
	border: none !important;
}

.chat-page .multiselect__input {
	background: transparent !important;
	border: none !important;
}

.chat-page .multiselect__content-wrapper {
	background: #fff !important;
	border: 1px solid #e2e8f0 !important;
	border-radius: 8px !important;
}

// =====================================================
// FILE MESSAGE STYLING - File attachment card display
// =====================================================
.vac-message-card {
	// Constrain file-only messages to not be too wide
	&:has(.vac-file-message) {
		max-width: 280px !important;
	}
}

.vac-file-message {
	display: flex !important;
	align-items: center !important;
	gap: 12px !important;
	padding: 10px 14px !important;
	background: rgba(0, 0, 0, 0.03) !important;
	border-radius: 8px !important;
	margin: 2px 0 !important;
	cursor: pointer !important;
	transition: background 0.15s ease !important;

	&:hover {
		background: rgba(0, 0, 0, 0.06) !important;
	}

	.vac-icon-file {
		flex-shrink: 0 !important;
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
		width: 36px !important;
		height: 36px !important;
		background: $brand-green !important;
		border-radius: 8px !important;

		svg {
			width: 20px !important;
			height: 20px !important;

			path {
				fill: #fff !important;
			}
		}
	}

	span {
		font-size: 13px !important;
		font-weight: 500 !important;
		color: #334155 !important;
		word-break: break-word !important;
		line-height: 1.3 !important;
		flex: 1 !important;
	}
}

// Current user file messages
.vac-message-current .vac-file-message {
	background: rgba(0, 0, 0, 0.04) !important;

	&:hover {
		background: rgba(0, 0, 0, 0.07) !important;
	}

	span {
		color: #1e293b !important;
	}
}

// =====================================================
// RECENT EMOJI ROW - Quick-access emoji strip
// =====================================================
.recent-emoji-row {
	display: flex !important;
	align-items: center !important;
	gap: 2px !important;
	padding: 4px 12px !important;
	border-top: 1px solid #f1f5f9 !important;
	background: #fafbfc !important;
	// Allow horizontal scroll when the row of emojis is wider than the
	// chat card. Keep on Smiling iPhone screenshot 2026-05-05: with
	// `justify-content: center` (old mobile rule) the emoji row spilled
	// past both edges of the visible area and clipped without giving the
	// user a way to scroll. Switching to overflow-x:auto + flex-start
	// alignment + flex-shrink:0 on each button means every emoji is
	// reachable, on touch via swipe and on desktop via wheel/trackpad.
	overflow-x: auto !important;
	overflow-y: hidden !important;
	-webkit-overflow-scrolling: touch !important;
	scrollbar-width: thin;

	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: rgba(148, 163, 184, 0.35);
		border-radius: 2px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}
}

.recent-emoji-header {
	font-size: 10px !important;
	font-weight: 600 !important;
	color: #94a3b8 !important;
	text-transform: uppercase !important;
	letter-spacing: 0.5px !important;
	flex-shrink: 0 !important;
	cursor: default !important;
	margin-right: 6px !important;
	// Sticky-left so the "QUICK ACTIONS" label stays anchored while the
	// emoji strip scrolls horizontally underneath it.
	position: sticky !important;
	left: 0 !important;
	background: #fafbfc !important;
	padding-right: 4px !important;
	z-index: 1 !important;
}

.recent-emoji-btn {
	cursor: pointer !important;
	font-size: 18px !important;
	padding: 3px 5px !important;
	border-radius: 6px !important;
	transition: background 0.15s ease, transform 0.1s ease !important;
	user-select: none !important;
	flex-shrink: 0 !important;

	&:hover {
		background: #e2e8f0 !important;
		transform: scale(1.15) !important;
	}

	&:active {
		transform: scale(0.95) !important;
	}
}

// Mobile: slightly larger tap targets. Keep flex-start so the row
// scrolls naturally from left to right instead of being centered
// (which clipped emojis off both sides on narrow viewports).
@media (max-width: 768px) {
	.recent-emoji-btn {
		font-size: 22px !important;
		padding: 5px 7px !important;
	}

	.recent-emoji-row {
		justify-content: flex-start !important;
		gap: 4px !important;
	}
}

// Image messages - inline preview
.vac-image-container {
	border-radius: 8px !important;
	overflow: hidden !important;
	cursor: pointer !important;

	img {
		max-width: 300px !important;
		border-radius: 8px !important;
	}
}
</style>